// Tab branding — title + favicon. Default is BrokerForYou; tenant-aware
// pages (portal, /site/:slug) override with the tenant's name and favicon_url.
export const DEFAULT_TITLE = 'BrokerForYou';
export const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ''}/favicon.svg`;

// A tenant logo/favicon is either an absolute URL, a legacy inline data: URI,
// or a path on the API (`/api/tenants/brand-asset/...`, ticket 203). The API
// lives on another host from the SPA, so a path gets the backend prefix.
export function brandAssetUrl(url) {
  if (!url) return url;
  return url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL || ''}${url}` : url;
}

// Ticket 154 P3: the tab title is base name + optional page label + unread
// prefix, so a count set by the layout survives applyBranding re-runs.
let baseTitle = DEFAULT_TITLE;
let pageLabel = '';
let unread = 0;

function render() {
  const prefix = unread > 0 ? `(${unread}) ` : '';
  const label = pageLabel ? `${pageLabel} · ` : '';
  document.title = `${prefix}${label}${baseTitle}`;
}

export function setTitleUnread(n) {
  unread = n > 0 ? Math.floor(n) : 0;
  render();
}

export function setTitlePage(label) {
  pageLabel = label || '';
  render();
}

export function applyBranding({ title, favicon } = {}) {
  baseTitle = title || DEFAULT_TITLE;
  render();
  const href = brandAssetUrl(favicon) || DEFAULT_FAVICON;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

// ---------------------------------------------------------------------------
// Staff portal chrome (ticket 203): the top bar follows the tenant's colors.
// The portal's dropdown panels render in a portal outside the header, so the
// colors live as CSS variables on <html>; markup reads `var(--brand-primary,
// #070B5F)` and falls back to the BrokerForYou navy and green when unset.
// ---------------------------------------------------------------------------
const HEX = /^#[0-9a-f]{6}$/i;
const CHROME_VARS = [
  '--brand-primary',
  '--brand-accent',
  '--brand-highlight',
  '--brand-on-accent',
  '--brand-on-highlight',
];

function luminance(hex) {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

// Readable text on a fill: the dark navy of the default badges, or white.
function textOn(hex) {
  return contrast(hex, '#030524') >= contrast(hex, '#ffffff') ? '#030524' : '#ffffff';
}

// The variables to set for a tenant `colors` object. The top bar's text is
// white, so a primary it cannot be read on (contrast under 4.5) is ignored.
export function chromeVars(colors) {
  const c = colors || {};
  const vars = {};
  if (HEX.test(c.primary || '') && contrast(c.primary, '#ffffff') >= 4.5) {
    vars['--brand-primary'] = c.primary;
  }
  if (HEX.test(c.accent || '')) {
    vars['--brand-accent'] = c.accent;
    vars['--brand-on-accent'] = textOn(c.accent);
  }
  if (HEX.test(c.highlight || '')) {
    vars['--brand-highlight'] = c.highlight;
    vars['--brand-on-highlight'] = textOn(c.highlight);
  }
  return vars;
}

export function applyChromeColors(colors) {
  const root = document.documentElement;
  const vars = chromeVars(colors);
  CHROME_VARS.forEach((name) => {
    if (vars[name]) root.style.setProperty(name, vars[name]);
    else root.style.removeProperty(name);
  });
}
